/**
 * SessionManager.tsx
 * 
 * Componente per la gestione completa della sessione utente.
 * 
 * FUNZIONALITÀ:
 * 1. Idle Detection - Rileva inattività utente
 * 2. Session Polling - Verifica periodica con il server
 * 3. Expiration Warning - Modale di avviso prima della scadenza
 * 4. Auto Logout - Logout automatico alla scadenza
 * 5. Activity Extension - Rinnovo sessione su attività
 * 
 * INSTALLAZIONE:
 * Wrappare l'app con questo componente dopo AuthProvider
 */

import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, ProgressBar } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { logout, resetAuthentication, me } from '@/redux/features/core/authentication/authenticationActions';
import { SESSION_EXPIRED_EVENT, checkSessionValid } from '../../axiosConfig';
import { routesIndex } from '@/RoutesIndex';
import * as defaults from '@/Defaults';

// =============================================================================
// CONFIGURAZIONE
// =============================================================================

const CONFIG = {
  // Timeout inattività (da Defaults.tsx)
  IDLE_TIMEOUT: defaults.app.inactivityTimeOut || 8 * 60 * 60 * 1000, // 8 ore default
  
  // Secondi di countdown prima del logout
  WARNING_COUNTDOWN: defaults.app.inactivityCountdown || 60, // 60 secondi default
  
  // Intervallo polling sessione (in ms)
  SESSION_POLL_INTERVAL: 5 * 60 * 1000, // 5 minuti
  
  // Mostra warning quando mancano X ms
  WARNING_THRESHOLD: 2 * 60 * 1000, // 2 minuti prima
  
  // Eventi che resettano il timer di inattività
  ACTIVITY_EVENTS: [
    'mousemove',
    'keydown',
    'wheel',
    'DOMMouseScroll',
    'mousewheel',
    'mousedown',
    'touchstart',
    'touchmove',
    'MSPointerDown',
    'MSPointerMove',
    'visibilitychange',
    'focus'
  ]
};

// =============================================================================
// INTERFACCE
// =============================================================================

interface SessionManagerProps {
  children: React.ReactNode;
  /** Disabilita il manager (utile per pagine pubbliche) */
  disabled?: boolean;
  /** Callback custom per logout */
  onSessionExpired?: () => void;
}

// =============================================================================
// COMPONENTE
// =============================================================================

const SessionManager: React.FC<SessionManagerProps> = ({ 
  children, 
  disabled = false,
  onSessionExpired 
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  const isAuthenticated = useAppSelector(
    (state) => state.authentication.data?.isAuthenticated
  );

  // State
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(CONFIG.WARNING_COUNTDOWN);
  const [isExpired, setIsExpired] = useState(false);

  // Refs
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  // ===========================================================================
  // HANDLERS
  // ===========================================================================

  /**
   * Gestisce il logout completo
   */
  const handleLogout = useCallback(async (reason: string = 'manual') => {
    console.log(`[SessionManager] Logout triggered: ${reason}`);
    
    // Pulisci intervals
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    // Chiudi modale
    setShowWarning(false);
    setIsExpired(true);

    // Dispatch logout
    dispatch(logout());
    dispatch(resetAuthentication());

    // Callback custom
    if (onSessionExpired) {
      onSessionExpired();
    }

    // Mostra toast
    toast.warning(t('messages.sessionExpired') || 'Sessione scaduta. Effettua nuovamente il login.', {
      position: 'top-center',
      autoClose: 5000,
    });

    // Redirect a login
    navigate(routesIndex.app.authentication, { 
      replace: true,
      state: { sessionExpired: true }
    });
  }, [dispatch, navigate, onSessionExpired, t]);

  /**
   * Estende la sessione (utente ha cliccato "Continua")
   */
  const handleExtendSession = useCallback(async () => {
    console.log('[SessionManager] Extending session...');
    
    // Pulisci countdown
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    // Chiudi modale
    setShowWarning(false);
    setCountdown(CONFIG.WARNING_COUNTDOWN);

    // Verifica sessione con server e refresh
    try {
      const result = await dispatch(me());
      if (result.meta.requestStatus === 'fulfilled') {
        lastActivityRef.current = Date.now();
        toast.success(t('messages.sessionExtended') || 'Sessione estesa con successo', {
          position: 'top-right',
          autoClose: 2000,
        });
      } else {
        // Sessione non più valida sul server
        handleLogout('session_invalid_on_extend');
      }
    } catch (error) {
      handleLogout('session_extend_error');
    }
  }, [dispatch, handleLogout, t]);

  /**
   * Avvia il countdown di warning
   */
  const startWarningCountdown = useCallback(() => {
    setShowWarning(true);
    setCountdown(CONFIG.WARNING_COUNTDOWN);

    countdownIntervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current!);
          handleLogout('idle_timeout');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [handleLogout]);

  // ===========================================================================
  // IDLE TIMER
  // ===========================================================================

  const onIdle = useCallback(() => {
    if (!isAuthenticated || disabled) return;
    
    console.log('[SessionManager] User went idle');
    startWarningCountdown();
  }, [isAuthenticated, disabled, startWarningCountdown]);

  const onActive = useCallback(() => {
    lastActivityRef.current = Date.now();
    
    // Se il warning è visibile ma l'utente è tornato attivo, 
    // NON chiudiamo automaticamente - deve cliccare "Continua"
  }, []);

  const onAction = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  const { reset: resetIdleTimer, getRemainingTime, isIdle } = useIdleTimer({
    timeout: CONFIG.IDLE_TIMEOUT - CONFIG.WARNING_THRESHOLD,
    onIdle,
    onActive,
    onAction,
    events: CONFIG.ACTIVITY_EVENTS,
    immediateEvents: [],
    //debounce: 500,
    throttle: 500,
    eventsThrottle: 200,
    startOnMount: true,
    startManually: false,
    stopOnIdle: false,
    crossTab: true,
    syncTimers: 200,
  });

  // ===========================================================================
  // SESSION POLLING
  // ===========================================================================

  useEffect(() => {
    if (!isAuthenticated || disabled) return;

    // Poll periodico della sessione
    pollIntervalRef.current = setInterval(async () => {
      const isValid = await checkSessionValid();
      
      if (!isValid) {
        console.log('[SessionManager] Session invalid on poll');
        handleLogout('session_poll_invalid');
      }
    }, CONFIG.SESSION_POLL_INTERVAL);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [isAuthenticated, disabled, handleLogout]);

  // ===========================================================================
  // LISTENER EVENTO SESSIONE SCADUTA (da axios interceptor)
  // ===========================================================================

  useEffect(() => {
    const handleSessionExpiredEvent = (event: CustomEvent) => {
      console.log('[SessionManager] Received session expired event:', event.detail);
      
      if (!isExpired) {
        handleLogout('axios_interceptor');
      }
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpiredEvent as EventListener);

    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpiredEvent as EventListener);
    };
  }, [handleLogout, isExpired]);

  // ===========================================================================
  // CLEANUP
  // ===========================================================================

  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // ===========================================================================
  // RESET QUANDO UTENTE SI AUTENTICA
  // ===========================================================================

  useEffect(() => {
    if (isAuthenticated) {
      setIsExpired(false);
      resetIdleTimer();
    }
  }, [isAuthenticated, resetIdleTimer]);

  // ===========================================================================
  // RENDER
  // ===========================================================================

  // Non mostrare nulla se disabilitato o non autenticato
  if (disabled || !isAuthenticated) {
    return <>{children}</>;
  }

  return (
    <>
      {children}

      {/* Modale di Warning */}
      <Modal
        show={showWarning}
        onHide={() => {}} // Non chiudibile con click esterno
        backdrop="static"
        keyboard={false}
        centered
        className="session-warning-modal"
      >
        <Modal.Header className="bg-warning text-dark">
          <Modal.Title className="d-flex align-items-center">
            <i className="mdi mdi-clock-alert-outline me-2 fs-4"></i>
            {t('labels.sessionExpiring') || 'Sessione in scadenza'}
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body className="text-center py-4">
          <div className="mb-4">
            <i className="mdi mdi-timer-sand text-warning" style={{ fontSize: '4rem' }}></i>
          </div>
          
          <p className="mb-3 fs-5">
            {t('messages.sessionExpiringIn') || 'La tua sessione scadrà tra'}
          </p>
          
          <div 
            className="display-1 fw-bold text-danger mb-3"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {countdown}
          </div>
          
          <p className="text-muted mb-3">
            {t('messages.sessionExpiringDescription') || 'secondi. Clicca "Continua" per mantenere la sessione attiva.'}
          </p>

          <ProgressBar 
            now={(countdown / CONFIG.WARNING_COUNTDOWN) * 100}
            variant={countdown > 30 ? 'warning' : 'danger'}
            animated
            className="mb-3"
            style={{ height: '8px' }}
          />
        </Modal.Body>
        
        <Modal.Footer className="justify-content-center">
          <Button 
            variant="outline-secondary" 
            onClick={() => handleLogout('user_chose_logout')}
            className="me-2"
          >
            <i className="mdi mdi-logout me-1"></i>
            {t('captions.logout') || 'Esci'}
          </Button>
          
          <Button 
            variant="success" 
            onClick={handleExtendSession}
            size="lg"
            autoFocus
          >
            <i className="mdi mdi-refresh me-1"></i>
            {t('captions.continue') || 'Continua'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SessionManager;
